// Main App component with LEGO-themed layout

import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import ChatInterface from './components/ChatInterface';
import AuthCallback from './components/AuthCallback';
import AnonymousUserButton from './components/AnonymousUserButton';
import UserProfile from './components/UserProfile';
import WelcomeMessage from './components/WelcomeMessage';
import UserSettingsPage from './pages/UserSettingsPage';
import RebrickableSetupPage from './pages/RebrickableSetupPage';
import BricksetSetupPage from './pages/BricksetSetupPage';
import EntityDetailPage from './pages/EntityDetailPage';
import BackofficePage from './pages/BackofficePage';
import { LoadingAnimationDemo } from './pages/LoadingAnimationDemo';
import { FacebookLoginDemo } from './pages/FacebookLoginDemo';
import { LoadingAnimation } from './components/LoadingAnimation';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './hooks/useAuth';
import { ROUTES } from './constants/routes';
import { CookieConsentBanner } from './components/CookieConsent';
import './styles/globals.css';

const AppContent: React.FC = () => {
  const { isAuthenticated, isAdmin, isLoading } = useAuth();
  const location = useLocation();

  // Store intended URL when unauthenticated user tries to access conversation
  useEffect(() => {
    if (!isLoading && !isAuthenticated && location.pathname.startsWith('/conversation/')) {
      sessionStorage.setItem('intendedUrl', location.pathname);
    }
  }, [isAuthenticated, isLoading, location.pathname]);

  return (
    <div className="app">
      {/* Header matching Jekyll design */}
      <header className="header">
        <div className="container">
          <div className="header-content">
            <Link to={ROUTES.HOME} className="logo-section" aria-label="Go to home page">
              <img
                src="/logo.svg"
                alt="Brick Directory Logo"
                className="logo"
                onError={(e) => {
                  // Fallback if logo is not available
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
              <div className="brand-text">
                Brick Directory Chat
                {location.pathname === ROUTES.BACKOFFICE && (
                  <span className="page-title"> - Admin Backoffice</span>
                )}
              </div>
            </Link>
            <div className="header-status">
              <span>🤖 AI-Powered LEGO Assistant</span>
              <div className="status-dot" />
              {isAuthenticated ? <UserProfile /> : <AnonymousUserButton />}
            </div>
          </div>
        </div>
      </header>

      {/* Floating Avatar - only visible on mobile */}
      {isAuthenticated && (
        <div className="floating-avatar">
          <UserProfile />
        </div>
      )}
      {!isAuthenticated && (
        <div className="floating-avatar">
          <AnonymousUserButton />
        </div>
      )}

      {/* Main content */}
      <main className="main-content">
        <Routes>
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path={ROUTES.LOADING_DEMO} element={<LoadingAnimationDemo />} />
          <Route path={ROUTES.FACEBOOK_DEMO} element={<FacebookLoginDemo />} />
          <Route
            path={ROUTES.BACKOFFICE}
            element={
              isLoading ? (
                <div className="loading-container">
                  <LoadingAnimation size={120} message="Loading..." />
                </div>
              ) : isAuthenticated && isAdmin() ? (
                <BackofficePage />
              ) : (
                <Navigate to={ROUTES.HOME} replace />
              )
            }
          />
          <Route
            path={ROUTES.ENTITY_DETAIL}
            element={
              isLoading ? (
                <div className="loading-container">
                  <LoadingAnimation size={120} message="Loading..." />
                </div>
              ) : isAuthenticated ? (
                <EntityDetailPage />
              ) : (
                <Navigate to={ROUTES.HOME} replace />
              )
            }
          />
          <Route
            path={ROUTES.SETTINGS}
            element={
              isLoading ? (
                <div className="loading-container">
                  <LoadingAnimation size={120} message="Loading..." />
                </div>
              ) : isAuthenticated ? (
                <UserSettingsPage />
              ) : (
                <Navigate to={ROUTES.HOME} replace />
              )
            }
          />
          <Route
            path={ROUTES.REBRICKABLE_SETUP}
            element={
              isLoading ? (
                <div className="loading-container">
                  <LoadingAnimation size={120} message="Loading..." />
                </div>
              ) : isAuthenticated ? (
                <RebrickableSetupPage />
              ) : (
                <Navigate to={ROUTES.HOME} replace />
              )
            }
          />
          <Route
            path="/setup/brickset"
            element={
              isLoading ? (
                <div className="loading-container">
                  <LoadingAnimation size={120} message="Loading..." />
                </div>
              ) : isAuthenticated ? (
                <BricksetSetupPage />
              ) : (
                <Navigate to={ROUTES.HOME} replace />
              )
            }
          />
          <Route
            path={ROUTES.CONVERSATION}
            element={
              isLoading ? (
                <div className="loading-container">
                  <LoadingAnimation size={120} message="Loading conversation..." />
                </div>
              ) : isAuthenticated ? (
                <ChatInterface />
              ) : (
                <Navigate to={ROUTES.HOME} replace />
              )
            }
          />
          <Route
            path={ROUTES.NEW_CONVERSATION}
            element={
              isLoading ? (
                <div className="loading-container">
                  <LoadingAnimation size={120} message="Loading..." />
                </div>
              ) : isAuthenticated ? (
                <ChatInterface />
              ) : (
                <Navigate to={ROUTES.HOME} replace />
              )
            }
          />
          <Route
            path={ROUTES.HOME}
            element={
              isLoading ? (
                <div className="loading-container">
                  <LoadingAnimation size={120} message="Loading..." />
                </div>
              ) : isAuthenticated ? (
                <Navigate to={ROUTES.NEW_CONVERSATION} replace />
              ) : (
                <WelcomeMessage />
              )
            }
          />
        </Routes>
      </main>

      {/* Footer - hidden via CSS */}
      <footer className="app-footer">
        <div className="container">
          <p>
            &copy; 2025 Vladimir Orany. Built upon{' '}
            <a
              href="https://rebrickable.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              Rebrickable
            </a>
            {' '}bricks.
          </p>
        </div>
      </footer>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div className="error-container">
          <h1>Oops! Something went wrong</h1>
          <p>We've been notified of the error and will fix it soon.</p>
          <details className="error-details">
            <summary>Error details</summary>
            <pre className="error-details-content">
              {error instanceof Error ? error.message : String(error)}
            </pre>
          </details>
          <button
            className="error-return-button"
            onClick={() => {
              resetError();
              window.location.href = '/';
            }}
          >
            Return to Home
          </button>
        </div>
      )}
      onError={(error) => {
        // Capture error with Sentry
        Sentry.captureException(error, {
          level: 'error',
          tags: {
            errorBoundary: 'App',
          },
        });
      }}
    >
      <Router>
        <AuthProvider>
          <CookieConsentBanner />
          <AppContent />
        </AuthProvider>
      </Router>
    </Sentry.ErrorBoundary>
  );
};

// Wrap App with Sentry Profiler and give it a display name for Fast Refresh
const ProfiledApp = Sentry.withProfiler(App);
ProfiledApp.displayName = 'ProfiledApp';

export default ProfiledApp;
